import { createContext, useContext, useCallback, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import type { CartItem } from "@/hooks/useCart";

export interface SavedOrder {
  orderRef: string;
  date: string;
  name: string;
  phone: string;
  city: string;
  address: string;
  notes: string;
  paymentLabel: string;
  items: CartItem[];
  totalPrice: number;
  discountCode: string;
  discountPercent: number;
  discountAmount: number;
  finalPrice: number;
  status: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
  customerId?: string;
}

interface OrdersContextType {
  getOrders: (phoneOrId: string, customerId?: string) => Promise<SavedOrder[]>;
  addOrder: (phone: string, order: SavedOrder, customerId?: string) => Promise<void>;
}

const OrdersContext = createContext<OrdersContextType | null>(null);

function rowToOrder(row: Record<string, unknown>): SavedOrder {
  return {
    orderRef: String(row.order_ref ?? ""),
    date: String(row.created_at ?? ""),
    name: String(row.name ?? ""),
    phone: String(row.phone ?? ""),
    city: String(row.city ?? ""),
    address: String(row.address ?? ""),
    notes: String(row.notes ?? ""),
    paymentLabel: String(row.payment_label ?? ""),
    items: Array.isArray(row.items) ? (row.items as CartItem[]) : [],
    totalPrice: Number(row.total_price ?? 0),
    discountCode: String(row.discount_code ?? ""),
    discountPercent: Number(row.discount_percent ?? 0),
    discountAmount: Number(row.discount_amount ?? 0),
    finalPrice: Number(row.final_price ?? 0),
    status: (row.status as SavedOrder["status"]) ?? "pending",
    customerId: row.customer_id ? String(row.customer_id) : undefined,
  };
}

function isRealPhone(phone: string): boolean {
  return !!phone && !phone.startsWith("email_") && phone.trim() !== "";
}

/** Returns all possible phone formats for a given number */
function phoneVariants(phone: string): string[] {
  const p = phone.replace(/[\s\-]/g, "");
  const variants = new Set<string>([p]);
  if (p.startsWith("05") && p.length === 10) {
    variants.add("+966" + p.slice(1));
    variants.add("966" + p.slice(1));
  } else if (p.startsWith("+9665")) {
    variants.add("0" + p.slice(4));
    variants.add(p.slice(1));
  } else if (p.startsWith("9665")) {
    variants.add("0" + p.slice(3));
    variants.add("+" + p);
  }
  return Array.from(variants);
}

export function OrdersProvider({ children }: { children: ReactNode }) {

  /**
   * Fetch orders for a customer.
   * - If customerId is provided → fetch by customer_id directly (covers email users)
   * - If phone is a real phone → also fetch by phone (covers legacy orders)
   * - Merges and deduplicates results
   */
  const getOrders = useCallback(async (
    phoneOrId: string,
    customerId?: string
  ): Promise<SavedOrder[]> => {
    try {
      const allRows: Record<string, unknown>[] = [];

      // Strategy 1: fetch by customer_id if available
      if (customerId && customerId.trim() !== "") {
        const { data: byId } = await supabase
          .from("orders")
          .select("*")
          .eq("customer_id", customerId)
          .order("created_at", { ascending: false });
        if (byId) allRows.push(...(byId as Record<string, unknown>[]));
      }

      // Strategy 2: fetch by phone (all variants) if it's a real phone number
      const phone = phoneOrId;
      if (isRealPhone(phone)) {
        const variants = phoneVariants(phone);
        for (const variant of variants) {
          const { data: byPhone } = await supabase
            .from("orders")
            .select("*")
            .eq("phone", variant)
            .order("created_at", { ascending: false });
          if (byPhone) allRows.push(...(byPhone as Record<string, unknown>[]));
        }

        // Strategy 3: also look up customer by phone and fetch by their customer_id
        if (!customerId) {
          const { data: customerData } = await supabase
            .from("customers")
            .select("id")
            .eq("phone", phone)
            .maybeSingle();

          if (customerData?.id) {
            const { data: byCustomerId } = await supabase
              .from("orders")
              .select("*")
              .eq("customer_id", customerData.id)
              .order("created_at", { ascending: false });
            if (byCustomerId) allRows.push(...(byCustomerId as Record<string, unknown>[]));

            // Auto-link unlinked phone orders
            const unlinked = allRows.filter(r => !r.customer_id && r.phone === phone);
            if (unlinked.length > 0) {
              const ids = unlinked.map(r => r.id as string).filter(Boolean);
              if (ids.length > 0) {
                await supabase
                  .from("orders")
                  .update({ customer_id: customerData.id })
                  .in("id", ids);
              }
            }
          }
        }
      }

      // Deduplicate by order_ref
      const seen = new Set<string>();
      const unique = allRows.filter(r => {
        const ref = String(r.order_ref);
        if (seen.has(ref)) return false;
        seen.add(ref);
        return true;
      });

      // Sort by date desc
      unique.sort((a, b) =>
        new Date(String(b.created_at)).getTime() - new Date(String(a.created_at)).getTime()
      );

      return unique.map(rowToOrder);
    } catch (e) {
      console.error("getOrders error:", e);
      return [];
    }
  }, []);

  /**
   * Save a new order and link it to the customer account if one exists.
   */
  const addOrder = useCallback(async (
    phone: string,
    order: SavedOrder,
    customerId?: string
  ): Promise<void> => {
    try {
      let resolvedCustomerId = customerId ?? null;

      // If no customerId passed and phone is real, try to find one by phone
      if (!resolvedCustomerId && isRealPhone(phone)) {
        const { data: customerData } = await supabase
          .from("customers")
          .select("id")
          .eq("phone", phone)
          .maybeSingle();
        resolvedCustomerId = customerData?.id ?? null;
      }

      await supabase.from("orders").insert({
        order_ref: order.orderRef,
        name: order.name,
        phone: order.phone,
        city: order.city,
        address: order.address,
        notes: order.notes,
        payment_label: order.paymentLabel,
        items: order.items,
        total_price: order.totalPrice,
        discount_code: order.discountCode,
        discount_percent: order.discountPercent,
        discount_amount: order.discountAmount,
        final_price: order.finalPrice,
        status: order.status,
        customer_id: resolvedCustomerId,
      });
    } catch (e) {
      console.error("addOrder error:", e);
    }
  }, []);

  return (
    <OrdersContext.Provider value={{ getOrders, addOrder }}>
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error("useOrders must be used within OrdersProvider");
  return ctx;
}
